import type { NostrEvent } from '@nostrify/nostrify';

/** Parsed Ditto theme from kind 16767 or 36767 event tags. */
export interface DittoTheme {
  /** Core colors extracted from `c` tags. */
  colors: {
    background: string; // hex e.g. "#1a1a2e"
    text: string;       // hex e.g. "#e0e0e0"
    primary: string;    // hex e.g. "#6c3ce0"
  };
  /** Optional fonts from `f` tags. */
  fonts?: {
    body?: { family: string; url: string };
    title?: { family: string; url: string };
  };
  /** Optional background from `bg` tag. */
  background?: {
    url: string;
    mode: 'cover' | 'tile';
    mime: string;
    dim?: string;
    blurhash?: string;
  };
  /** Theme title from `title` tag. */
  title?: string;
  /** Source theme reference from `a` tag. */
  source?: string;
}

/**
 * Parse a kind 16767 or 36767 event into a DittoTheme.
 * Returns undefined if the event doesn't have the required 3 color tags.
 */
export function parseDittoTheme(event: NostrEvent): DittoTheme | undefined {
  const colors: Partial<DittoTheme['colors']> = {};
  let fonts: DittoTheme['fonts'];
  let background: DittoTheme['background'];
  let title: string | undefined;
  let source: string | undefined;

  for (const tag of event.tags) {
    if (tag[0] === 'c' && tag[1] && tag[2]) {
      const hex = tag[1];
      const role = tag[2];
      if (role === 'background') colors.background = hex;
      if (role === 'text') colors.text = hex;
      if (role === 'primary') colors.primary = hex;
    }

    if (tag[0] === 'f' && tag[1] && tag[2]) {
      const family = tag[1];
      const url = tag[2];
      // Role is tag[3]; legacy events without role default to "body"
      const role = tag[3] || 'body';
      if (!fonts) fonts = {};
      if (role === 'body') fonts.body = { family, url };
      if (role === 'title') fonts.title = { family, url };
    }

    if (tag[0] === 'bg') {
      const bgData: Record<string, string> = {};
      for (let i = 1; i < tag.length; i++) {
        const part = tag[i];
        const spaceIdx = part.indexOf(' ');
        if (spaceIdx > 0) {
          const key = part.slice(0, spaceIdx);
          const value = part.slice(spaceIdx + 1);
          bgData[key] = value;
        }
      }
      if (bgData['url'] && bgData['mode'] && bgData['m']) {
        background = {
          url: bgData['url'],
          mode: bgData['mode'] as 'cover' | 'tile',
          mime: bgData['m'],
          dim: bgData['dim'],
          blurhash: bgData['blurhash'],
        };
      }
    }

    if (tag[0] === 'title' && tag[1]) {
      title = tag[1];
    }

    if (tag[0] === 'a' && tag[1]) {
      source = tag[1];
    }
  }

  // All 3 colors are required
  if (!colors.background || !colors.text || !colors.primary) {
    return undefined;
  }

  return {
    colors: colors as DittoTheme['colors'],
    fonts,
    background,
    title,
    source,
  };
}

/**
 * Convert a hex color string like "#1a1a2e" to HSL components "230 33% 14%"
 * suitable for CSS custom properties used by shadcn/ui.
 */
function hexToHSL(hex: string): string {
  // Remove the # prefix
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16) / 255;
  const g = parseInt(h.substring(2, 4), 16) / 255;
  const b = parseInt(h.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let hue = 0;
  let sat = 0;
  const lit = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    sat = lit > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: hue = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: hue = ((b - r) / d + 2) / 6; break;
      case b: hue = ((r - g) / d + 4) / 6; break;
    }
  }

  return `${Math.round(hue * 360)} ${Math.round(sat * 100)}% ${Math.round(lit * 100)}%`;
}

/**
 * Parse a hex color to RGB values [0-255].
 */
function hexToRGB(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
}

/**
 * Compute relative luminance of a hex color.
 */
function luminance(hex: string): number {
  const [r, g, b] = hexToRGB(hex).map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Determine if a background color is dark.
 */
function isDark(hex: string): boolean {
  return luminance(hex) < 0.3;
}

/**
 * Mix two hex colors. Factor 0 = color1, 1 = color2.
 */
function mixHex(color1: string, color2: string, factor: number): string {
  const [r1, g1, b1] = hexToRGB(color1);
  const [r2, g2, b2] = hexToRGB(color2);
  const r = Math.round(r1 + (r2 - r1) * factor);
  const g = Math.round(g1 + (g2 - g1) * factor);
  const b = Math.round(b1 + (b2 - b1) * factor);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/**
 * Get a contrasting foreground color (white or dark) for a given background.
 */
function contrastForeground(bgHex: string): string {
  return isDark(bgHex) ? '#ffffff' : '#1a1a2e';
}

/**
 * Generate all CSS custom properties from a DittoTheme.
 * These map to the CSS variables used by shadcn/ui.
 *
 * From just 3 colors (background, text, primary) we derive:
 * card, popover, secondary, muted, accent, border, input, ring, destructive, etc.
 */
export function themeToCSSProperties(theme: DittoTheme): Record<string, string> {
  const { background: bg, text, primary } = theme.colors;
  const dark = isDark(bg);

  // Derive intermediate colors
  const card = dark ? mixHex(bg, '#ffffff', 0.04) : mixHex(bg, '#ffffff', 0.5);
  const popover = card;
  const muted = dark ? mixHex(bg, '#ffffff', 0.08) : mixHex(bg, '#000000', 0.06);
  const mutedFg = dark ? mixHex(text, bg, 0.4) : mixHex(text, bg, 0.4);
  const accent = dark ? mixHex(primary, bg, 0.85) : mixHex(primary, bg, 0.88);
  const border = dark ? mixHex(bg, '#ffffff', 0.12) : mixHex(bg, '#000000', 0.1);
  const input = border;
  const secondary = dark ? mixHex(bg, '#ffffff', 0.1) : mixHex(bg, '#000000', 0.08);
  const secondaryFg = text;
  const primaryFg = contrastForeground(primary);
  const destructive = '#ef4444';
  const destructiveFg = '#ffffff';

  return {
    '--background': hexToHSL(bg),
    '--foreground': hexToHSL(text),
    '--card': hexToHSL(card),
    '--card-foreground': hexToHSL(text),
    '--popover': hexToHSL(popover),
    '--popover-foreground': hexToHSL(text),
    '--primary': hexToHSL(primary),
    '--primary-foreground': hexToHSL(primaryFg),
    '--secondary': hexToHSL(secondary),
    '--secondary-foreground': hexToHSL(secondaryFg),
    '--muted': hexToHSL(muted),
    '--muted-foreground': hexToHSL(mutedFg),
    '--accent': hexToHSL(accent),
    '--accent-foreground': hexToHSL(text),
    '--destructive': hexToHSL(destructive),
    '--destructive-foreground': hexToHSL(destructiveFg),
    '--border': hexToHSL(border),
    '--input': hexToHSL(input),
    '--ring': hexToHSL(primary),
    '--sidebar-background': hexToHSL(bg),
    '--sidebar-foreground': hexToHSL(text),
    '--sidebar-primary': hexToHSL(primary),
    '--sidebar-primary-foreground': hexToHSL(primaryFg),
    '--sidebar-accent': hexToHSL(muted),
    '--sidebar-accent-foreground': hexToHSL(text),
    '--sidebar-border': hexToHSL(border),
    '--sidebar-ring': hexToHSL(primary),
  };
}

/**
 * Convert a hex color to space-separated RGB channels for Tailwind CSS variable support.
 * e.g. "#1a1a2e" → "26 26 46"
 */
function hexToRGBChannels(hex: string): string {
  const [r, g, b] = hexToRGB(hex);
  return `${r} ${g} ${b}`;
}

/**
 * Generate the Polaroids-specific color overrides from a theme.
 * These map to the custom color tokens used in tailwind.config (cream, pol-text, etc.)
 * Values are space-separated RGB channels for Tailwind opacity modifier support.
 */
export function themeToPolColors(theme: DittoTheme): Record<string, string> {
  const { background: bg, text, primary } = theme.colors;
  const dark = isDark(bg);

  // Derive polaroid-specific tokens
  const creamDark = dark ? mixHex(bg, '#ffffff', 0.1) : mixHex(bg, '#000000', 0.06);
  const polaroidWhite = dark ? mixHex(bg, '#ffffff', 0.08) : mixHex(bg, '#ffffff', 0.7);
  const polTextSecondary = dark ? mixHex(text, bg, 0.35) : mixHex(text, bg, 0.4);
  const polTextCaption = dark ? mixHex(primary, '#ffffff', 0.3) : mixHex(primary, '#000000', 0.15);
  const primaryLight = dark ? mixHex(primary, '#ffffff', 0.2) : mixHex(primary, bg, 0.3);

  return {
    '--pol-bg': hexToRGBChannels(bg),
    '--pol-bg-dark': hexToRGBChannels(creamDark),
    '--pol-white': hexToRGBChannels(polaroidWhite),
    '--pol-text-color': hexToRGBChannels(text),
    '--pol-text-secondary-color': hexToRGBChannels(polTextSecondary),
    '--pol-text-caption-color': hexToRGBChannels(polTextCaption),
    '--pol-primary': hexToRGBChannels(primary),
    '--pol-primary-light': hexToRGBChannels(primaryLight),
  };
}

// ---------------------------------------------------------------------------
// Tonal helpers — adjust lightness/saturation WITHOUT changing hue
// ---------------------------------------------------------------------------

/** Lighten a hex color by mixing toward white. `amount` 0–1. */
function lighten(hex: string, amount: number): string {
  return mixHex(hex, '#ffffff', amount);
}

/** Darken a hex color by mixing toward black. `amount` 0–1. */
function darken(hex: string, amount: number): string {
  return mixHex(hex, '#000000', amount);
}

/**
 * Create a tape-friendly tint of a color.
 * Light themes → lighten toward white for a pastel feel.
 * Dark themes  → lighten moderately so the tape is visible on dark bg.
 */
function tapeTint(hex: string, dark: boolean): string {
  return dark ? lighten(hex, 0.3) : lighten(hex, 0.45);
}

/**
 * Create a slightly lighter highlight variant (for stripe alternation).
 */
function tapeHighlight(hex: string, dark: boolean): string {
  return dark ? lighten(hex, 0.15) : lighten(hex, 0.2);
}

/** Number of tape variants in the system. */
export const TAPE_VARIANT_COUNT = 8;

/**
 * Generate theme-derived washi tape colors (8 variants).
 *
 * Uses ONLY the 3 theme colors (background, text, primary) — preserving
 * their exact hues. Variety comes from tonal shifts (lighter / darker /
 * more muted) and cross-mixing the theme colors together.
 *
 * When a background image is present, extracted accent colors from that
 * image are used for variants 6 & 7 (real colors from the wallpaper).
 *
 * Color distribution — all hues stay on-theme:
 *   0 — Primary light tint                     → glitter sparkle
 *   1 — Text light tint                        → diagonal stripes
 *   2 — Rainbow: primary, text, mix, + tints   → rainbow stripes
 *   3 — Primary ↔ Text mix (50/50)             → polkadot
 *   4 — Primary darker tone                    → diagonal stripes
 *   5 — Text darker tone                       → checkerboard
 *   6 — BG-image accent OR primary ↔ bg mix    → crosshatch
 *   7 — BG-image accent OR text ↔ bg mix       → zigzag
 */
export function themeToTapeColors(
  theme: DittoTheme,
  extraColors?: string[],
): Record<string, string> {
  const { background: bg, text, primary } = theme.colors;
  const dark = isDark(bg);

  // --- Variant 0: Primary light tint (glitter sparkle) ---
  const v0Base = tapeTint(primary, dark);
  const v0Hi   = tapeHighlight(v0Base, dark);

  // --- Variant 1: Text light tint (diagonal stripes) ---
  const v1Base = tapeTint(text, dark);
  const v1Hi   = tapeHighlight(v1Base, dark);

  // --- Variant 2: Rainbow — 5 stripes from the theme's actual colors ---
  const rainbowA = tapeTint(primary, dark);                       // primary tint
  const rainbowB = tapeTint(text, dark);                          // text tint
  const rainbowC = tapeTint(mixHex(primary, text, 0.5), dark);   // primary↔text midpoint
  const rainbowD = dark ? lighten(primary, 0.5) : lighten(primary, 0.6); // primary very light
  const rainbowE = dark ? lighten(text, 0.5) : lighten(text, 0.6);       // text very light

  // --- Variant 3: Primary ↔ Text cross-mix (polkadot) ---
  const v3Base = tapeTint(mixHex(primary, text, 0.5), dark);

  // --- Variant 4: Primary darker tone (diagonal stripes) ---
  const v4Base = dark ? lighten(primary, 0.15) : darken(primary, 0.08);
  const v4Hi   = tapeHighlight(v4Base, dark);

  // --- Variant 5: Text darker tone (checkerboard) ---
  const v5Base = dark ? lighten(text, 0.15) : darken(text, 0.05);
  const v5Hi   = tapeHighlight(v5Base, dark);

  // --- Variant 6: BG-image accent OR primary ↔ background mix (crosshatch) ---
  const v6Raw  = extraColors?.[0] ?? mixHex(primary, bg, 0.4);
  const v6Base = tapeTint(v6Raw, dark);
  const v6Hi   = tapeHighlight(v6Base, dark);

  // --- Variant 7: BG-image accent OR text ↔ background mix (zigzag) ---
  const v7Raw  = extraColors?.[1] ?? mixHex(text, bg, 0.4);
  const v7Base = tapeTint(v7Raw, dark);
  const v7Hi   = tapeHighlight(v7Base, dark);

  return {
    '--tape-0-base': v0Base,
    '--tape-0-hi': v0Hi,
    '--tape-0-solid': v0Base,
    '--tape-1-base': v1Base,
    '--tape-1-hi': v1Hi,
    '--tape-1-solid': v1Base,
    '--tape-rainbow-a': rainbowA,
    '--tape-rainbow-b': rainbowB,
    '--tape-rainbow-c': rainbowC,
    '--tape-rainbow-d': rainbowD,
    '--tape-rainbow-e': rainbowE,
    '--tape-2-solid': rainbowA,
    '--tape-3-base': v3Base,
    '--tape-3-solid': v3Base,
    '--tape-4-base': v4Base,
    '--tape-4-hi': v4Hi,
    '--tape-4-solid': v4Base,
    '--tape-5-base': v5Base,
    '--tape-5-hi': v5Hi,
    '--tape-5-solid': v5Base,
    '--tape-6-base': v6Base,
    '--tape-6-hi': v6Hi,
    '--tape-6-solid': v6Base,
    '--tape-7-base': v7Base,
    '--tape-7-hi': v7Hi,
    '--tape-7-solid': v7Base,
  };
}

/**
 * Extract dominant colors from a background image URL.
 * Draws the image onto a small canvas, samples pixels, and clusters
 * them to find 2–4 representative hues (excluding near-grayscale).
 * Returns an array of hex strings, or empty if extraction fails.
 */
export function extractImageColors(imageUrl: string): Promise<string[]> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    const timeout = setTimeout(() => resolve([]), 5000);

    img.onload = () => {
      clearTimeout(timeout);
      try {
        const SIZE = 64; // sample at 64×64
        const canvas = document.createElement('canvas');
        canvas.width = SIZE;
        canvas.height = SIZE;
        const ctx = canvas.getContext('2d');
        if (!ctx) { resolve([]); return; }

        ctx.drawImage(img, 0, 0, SIZE, SIZE);
        const data = ctx.getImageData(0, 0, SIZE, SIZE).data;

        // Collect saturated pixels (skip near-grayscale)
        const pixels: [number, number, number][] = [];
        for (let i = 0; i < data.length; i += 16) { // sample every 4th pixel
          const r = data[i], g = data[i + 1], b = data[i + 2];
          const max = Math.max(r, g, b), min = Math.min(r, g, b);
          const saturation = max === 0 ? 0 : (max - min) / max;
          const lightness = (max + min) / 510;
          // Only keep reasonably saturated, non-extreme-brightness pixels
          if (saturation > 0.15 && lightness > 0.1 && lightness < 0.9) {
            pixels.push([r, g, b]);
          }
        }

        if (pixels.length < 8) { resolve([]); return; }

        // Simple k-means with k=4, 8 iterations
        const k = 4;
        const centroids: [number, number, number][] = [];
        for (let i = 0; i < k; i++) {
          centroids.push(pixels[Math.floor(i * pixels.length / k)]);
        }

        for (let iter = 0; iter < 8; iter++) {
          const buckets: [number, number, number][][] = Array.from({ length: k }, () => []);
          for (const px of pixels) {
            let bestDist = Infinity, bestIdx = 0;
            for (let ci = 0; ci < k; ci++) {
              const dr = px[0] - centroids[ci][0];
              const dg = px[1] - centroids[ci][1];
              const db = px[2] - centroids[ci][2];
              const dist = dr * dr + dg * dg + db * db;
              if (dist < bestDist) { bestDist = dist; bestIdx = ci; }
            }
            buckets[bestIdx].push(px);
          }
          for (let ci = 0; ci < k; ci++) {
            if (buckets[ci].length === 0) continue;
            let sr = 0, sg = 0, sb = 0;
            for (const px of buckets[ci]) { sr += px[0]; sg += px[1]; sb += px[2]; }
            const n = buckets[ci].length;
            centroids[ci] = [sr / n, sg / n, sb / n];
          }
        }

        // Convert centroids to hex, sort by saturation (most vivid first)
        const results = centroids
          .map(([r, g, b]) => {
            const hex = `#${Math.round(r).toString(16).padStart(2, '0')}${Math.round(g).toString(16).padStart(2, '0')}${Math.round(b).toString(16).padStart(2, '0')}`;
            const max = Math.max(r, g, b), min = Math.min(r, g, b);
            const sat = max === 0 ? 0 : (max - min) / max;
            return { hex, sat };
          })
          .filter(({ sat }) => sat > 0.12)
          .sort((a, b) => b.sat - a.sat)
          .map(({ hex }) => hex);

        resolve(results.slice(0, 4));
      } catch {
        resolve([]);
      }
    };

    img.onerror = () => {
      clearTimeout(timeout);
      resolve([]);
    };

    img.src = imageUrl;
  });
}

/**
 * Check if a MIME type is a video type.
 */
export function isVideoBg(mime: string): boolean {
  return mime.startsWith('video/');
}
